import React, { useEffect, useState } from "react";
import { Loader2, Save, Film, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { apiErr } from "../../lib/apiErr";

const FIELDS = [
  { key: "video_mb", label: "Video", icon: Film, max: 500, presets: [50, 100, 250, 500], hint: "Batas ukuran per file video (1–500 MB)" },
  { key: "attachment_mb", label: "Lampiran umum", icon: Paperclip, max: 100, presets: [10, 25, 50, 100], hint: "Dokumen, gambar & file lain (1–100 MB)" },
];
const IN = "w-full rounded-xl border border-line bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand disabled:bg-slate-50";

function LimitField({ f, value, onChange, readOnly }) {
  const Icon = f.icon;
  const n = Number(value);
  const bad = value === "" || !Number.isInteger(n) || n < 1 || n > f.max;
  return (
    <div className="rounded-2xl border border-line p-4" data-testid={`limit-${f.key}`}>
      <p className="flex items-center gap-2 text-sm font-bold text-slate-900"><Icon size={15} className="text-brand" /> {f.label}</p>
      <p className="text-[11px] text-slate-400">{f.hint}</p>
      <div className="mt-3 flex items-center gap-3">
        <input type="range" min={1} max={f.max} disabled={readOnly} value={bad ? 1 : n} onChange={(e) => onChange(Number(e.target.value))} className="flex-1 accent-brand" data-testid={`limit-${f.key}-range`} />
        <div className="relative w-28">
          <input type="number" min={1} max={f.max} step={1} disabled={readOnly} value={value} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} className={`${IN} pr-10 ${bad ? "border-red-300" : ""}`} data-testid={`limit-${f.key}-input`} />
          <span className="pointer-events-none absolute right-3 top-2 text-sm text-slate-400">MB</span>
        </div>
      </div>
      {!readOnly && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {f.presets.map((p) => <button key={p} type="button" onClick={() => onChange(p)} data-testid={`limit-${f.key}-preset-${p}`} className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${n === p ? "bg-slate-900 text-white" : "border border-line bg-white text-slate-600 hover:border-brand hover:text-brand"}`}>{p} MB</button>)}
        </div>
      )}
      {bad && <p className="mt-1 text-[11px] text-red-600" data-testid={`limit-${f.key}-error`}>Harus bilangan bulat 1–{f.max}</p>}
    </div>
  );
}

export default function UploadLimits({ readOnly }) {
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.get("/admin/upload-limits").then((r) => { setSaved(r.data); setForm({ video_mb: r.data.video_mb, attachment_mb: r.data.attachment_mb }); })
      .catch((e) => setError(apiErr(e, "Gagal memuat batas upload")));
  }, []);
  const valid = form && FIELDS.every((f) => Number.isInteger(form[f.key]) && form[f.key] >= 1 && form[f.key] <= f.max);
  const dirty = form && saved && FIELDS.some((f) => form[f.key] !== saved[f.key]);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/admin/upload-limits", { ...form, ...(note.trim() && { note: note.trim() }) });
      const d = { ...saved, ...form, ...r.data };
      setSaved(d); setForm({ video_mb: d.video_mb, attachment_mb: d.attachment_mb }); setNote("");
      toast.success("Batas upload disimpan — berlaku untuk web & mobile");
    } catch (e) {
      const s = e?.response?.status;
      toast.error(s === 403 ? "Hanya Super Admin yang dapat mengubah batas upload" : s === 422 ? "Nilai di luar rentang yang diizinkan" : apiErr(e, "Gagal menyimpan"));
    } finally { setBusy(false); }
  };
  return (
    <div className="aivora-card p-6" data-testid="upload-limits">
      <h2 className="text-sm font-bold text-slate-900">Batas maksimal upload</h2>
      <p className="mt-1 text-xs text-slate-500">Dipakai aplikasi pengguna (web & mobile) lewat <code>GET /api/upload-limits</code> untuk memvalidasi ukuran file sebelum diunggah.</p>
      {error && <p className="mt-4 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="upload-limits-error">{error}</p>}
      {!form && !error && <Loader2 size={14} className="mt-4 animate-spin text-slate-400" />}
      {form && (
        <>
          {readOnly && <p className="mt-4 rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700">Mode baca — hanya Super Admin yang dapat mengubah.</p>}
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            {FIELDS.map((f) => <LimitField key={f.key} f={f} value={form[f.key]} readOnly={readOnly} onChange={(v) => setForm((x) => ({ ...x, [f.key]: v }))} />)}
          </div>
          {!readOnly && (
            <div className="mt-4 flex flex-wrap items-end gap-3">
              <label className="min-w-[240px] flex-1"><span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Catatan perubahan (opsional)</span><input className={`${IN} mt-1`} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Alasan perubahan, tercatat di audit" data-testid="upload-limits-note" /></label>
              <button onClick={() => setForm({ video_mb: saved.video_mb, attachment_mb: saved.attachment_mb })} disabled={!dirty || busy} className="rounded-xl border border-line bg-white px-4 py-2 text-xs font-semibold text-slate-600 disabled:opacity-40" data-testid="upload-limits-reset">Batalkan</button>
              <button onClick={save} disabled={!dirty || !valid || busy} className="btn-grad flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-bold disabled:opacity-40" data-testid="upload-limits-save">{busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Simpan</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
